import React, { createContext, useState, useContext, useRef } from "react";
import { GetAccountMetadata, UpdateAccountMetadata } from "@/Utils/Promises";
import { appstr } from "@coreapp/utils/appstr";
import { useToast } from "@/providers/ToastProvider";
import { GetWSMgr, UpdateChartList } from "@/Utils/StateLib";
import { useLocation } from "react-router-dom";
const AccountSettingsContext = createContext();

export const sanitizeBaseChartList = data => {
  if (!data.charts) return data;

  for (let chartIDx in data.charts) {
    let chart = data.charts[chartIDx];
    if (!chart.fullChart) {
      data.charts[chartIDx] = { ID: chart.ID, name: chart.name };
    }
  }

  return data;
};

const AccountSettingsProvider = ({ children }) => {
  const [accountConfig, setAccountConfig] = useState(null);
  const [clonedAccountConfig, setClonedAccountConfig] = React.useState(null);
  const accountConfigRef = useRef(null);

  const { addToast } = useToast();

  const location = useLocation();
  React.useEffect(() => {
    const wsMgr = GetWSMgr();
    if (!wsMgr?.fUserInfo) return;
    if (location.pathname.includes("settings")) {
      LoadAccountMetadata();
    }
  }, [location]);

  const updateAccountConfigRef = nextConfigData => {
    setAccountConfig(nextConfigData);
    accountConfigRef.current = nextConfigData;
  };

  const SaveAccountConfigChanges = async (data, options = {}) => {
    const { nextSection, closeAfterSave, handleTabChange, onCloseModal, setChangedSections } =
      options;
    data = structuredClone(data);
    const { success } = await UpdateAccountMetadata(JSON.stringify(data));
    if (success) {
      const nextConfig = { ...accountConfigRef.current, ...data };
      updateAccountConfigRef(nextConfig);
      setClonedAccountConfig(structuredClone(nextConfig));

      // Sync theme settings with wsMgr.fOrgInfo
      const wsMgr = GetWSMgr();

      if (data.theme) {
        try {
          wsMgr?.UpdateMetadata({ theme: data.theme });
        } catch (e) {
          console.error("Failed to sync setting changes with account metadata:", e);
        }
      }
      if (setChangedSections) setChangedSections([]);
      if (nextSection != null && handleTabChange) {
        handleTabChange(nextSection);
      }
      if (data.charts) {
        UpdateChartList(data.charts);
      }
      addToast({ message: appstr("Changes have been saved"), status: "success" });
      if (closeAfterSave && onCloseModal) onCloseModal();
    } else {
      addToast({ message: appstr("An error occurred while saving"), status: "error" });
    }
  };

  const LoadAccountMetadata = async () => {
    const { result } = await GetAccountMetadata();
    setAccountConfig(result);
    setClonedAccountConfig(structuredClone(result));
  };

  const ReportLaunchAccountSettingsEvent = () => {
    const wsMgr = GetWSMgr();
    const eventTracker = wsMgr.GetUsageTracker();
    eventTracker.ReportEvent("account_settings.settings.opened");
  };

  return (
    <AccountSettingsContext.Provider
      value={{
        accountConfig,
        accountConfigRef,
        clonedAccountConfig,
        setClonedAccountConfig,
        setAccountConfig,
        updateAccountConfigRef,
        SaveAccountConfigChanges,
        ReportLaunchAccountSettingsEvent,
        LoadAccountMetadata
      }}
    >
      {children}
    </AccountSettingsContext.Provider>
  );
};

export const useAccountSettingsContext = () => useContext(AccountSettingsContext);

export default AccountSettingsProvider;
